import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, ListOrdered, Loader2, Globe2 } from 'lucide-react'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { useFilial } from '@/lib/filial'
import toast from 'react-hot-toast'

interface Cfop {
  id?: number; codigo: string; descricao: string
  // null = vale pra todas as filiais (é o caso de todo CFOP anterior a 08/10)
  filial_id?: number | null
  tipo: string | null; cst_sugerido: string | null; observacao: string | null; ativo: boolean
  // Configuração da operação — o que faz este CFOP virar uma opção na Nota Avulsa
  natureza_padrao?: string | null
  cfop_par?: string | null
  gera_estoque?: boolean
  gera_financeiro?: boolean
  consumidor_final?: string | null
  finalidade?: string | null
  observacao_operacao?: string | null
  exige_retorno?: boolean
  cfop_retorno?: string | null
  prazo_retorno_dias?: number | null
}
const EMPTY: Cfop = {
  codigo: '', descricao: '', tipo: 'saida', cst_sugerido: '', observacao: '', ativo: true,
  filial_id: null,
  natureza_padrao: '', cfop_par: '', gera_estoque: false, gera_financeiro: false,
  consumidor_final: '', finalidade: '1', observacao_operacao: '',
  exige_retorno: false, cfop_retorno: '', prazo_retorno_dias: 180,
}

export default function CfopPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState<Cfop | null>(null)
  // Consolidado = o usuário administra o cadastro das lojas todas, então ESCOLHE
  // a filial. Dentro de uma filial, o backend carimba sozinho (set_filial) e o
  // select nem aparece — menos um campo pra errar no dia a dia.
  const { filialAtual, filiais, modoConsolidado } = useFilial()
  const consolidado = modoConsolidado || !filialAtual
  const nomeFilial = (id?: number | null) =>
    id ? (filiais.find((f: any) => f.id === id)?.nome_fantasia
          || filiais.find((f: any) => f.id === id)?.razao_social
          || `Filial ${id}`)
       : 'Todas as filiais'

  const { data: cfops = [], isLoading } = useQuery<Cfop[]>({
    queryKey: ['cfops'],
    queryFn: async () => (await api.get('/fiscal/cfops')).data,
  })

  const salvar = useMutation({
    mutationFn: async (c: Cfop) => c.id
      ? (await api.put('/fiscal/cfops/' + c.id, c)).data
      : (await api.post('/fiscal/cfops', c)).data,
    onSuccess: () => { toast.success('CFOP salvo'); setForm(null); qc.invalidateQueries({ queryKey: ['cfops'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete('/fiscal/cfops/' + id)).data,
    onSuccess: () => { toast.success('CFOP excluído'); qc.invalidateQueries({ queryKey: ['cfops'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })
  const seed = useMutation({
    mutationFn: async () => (await api.post('/fiscal/cfops/seed-nota-avulsa')).data,
    onSuccess: (d: any) => {
      toast.success(`${d.criados} CFOP(s) cadastrado(s)`)
      qc.invalidateQueries({ queryKey: ['cfops'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cadastrar'),
  })

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2"><ListOrdered size={24} /> CFOP</h1>
          <p className="text-xs text-ink-500 mt-0.5">
            CFOP com <b>natureza</b> preenchida vira uma opção no seletor da Nota Fiscal Avulsa.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" disabled={seed.isPending}
            title="Cadastra os CFOPs mais usados em nota avulsa (transferência, remessa p/ conserto, devolução...). Não sobrescreve o que já existe."
            onClick={() => {
              if (window.confirm('Cadastrar os CFOPs padrão de nota avulsa? CFOPs já existentes não são alterados.')) {
                seed.mutate()
              }
            }}>
            {seed.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} CFOPs de nota avulsa
          </button>
          <button className="btn-primary" onClick={() => setForm({ ...EMPTY })}><Plus size={14} /> Novo CFOP</button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Código</th><th>Descrição</th><th>Filial</th><th>Tipo</th><th>Operação (nota avulsa)</th><th>Par outra UF</th><th>CST</th><th>Ativo</th><th></th></tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="text-center py-8 text-ink-400"><Loader2 className="animate-spin inline" size={16} /> Carregando...</td></tr>
            ) : cfops.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-ink-400">Nenhum CFOP cadastrado</td></tr>
            ) : cfops.map(c => (
              <tr key={c.id} className="hover:bg-ink-50 group">
                <td className="font-mono">{c.codigo}</td>
                <td>{c.descricao}</td>
                <td className="text-xs">
                  {c.filial_id
                    ? nomeFilial(c.filial_id)
                    : <span className="inline-flex items-center gap-1 text-ink-500">
                        <Globe2 size={11} /> Todas
                      </span>}
                </td>
                <td className="capitalize">{c.tipo || '—'}</td>
                <td className="text-xs">
                  {c.natureza_padrao ? (
                    <span className="inline-flex items-center gap-1">
                      {c.natureza_padrao}
                      {c.gera_estoque && <span className="px-1 rounded bg-sky-100 text-sky-700 text-[10px]">estoque</span>}
                      {c.gera_financeiro && <span className="px-1 rounded bg-emerald-100 text-emerald-700 text-[10px]">financeiro</span>}
                    </span>
                  ) : <span className="text-ink-300">— só catálogo</span>}
                </td>
                <td className="font-mono text-xs">{c.cfop_par || '—'}</td>
                <td className="font-mono text-xs">{c.cst_sugerido || '—'}</td>
                <td>{c.ativo ? '✓' : '—'}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-brand-600 px-1" onClick={() => setForm({ ...c })}><Pencil size={14} /></button>
                  <button className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-red-600 px-1" onClick={() => c.id && pedirExclusao({ descricao: `o CFOP ${c.codigo}`, onConfirm: () => excluir.mutate(c.id!) })}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          {/* Altura máxima + flex-col: o modal cresce bastante quando a natureza é
              preenchida (aparecem CFOP par, finalidade, consumidor final, retorno,
              dica...). Sem limite, em monitor menor os botões Cancelar/Criar ficavam
              fora da tela. Agora só o miolo rola — cabeçalho e botões ficam fixos. */}
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b shrink-0"><h3 className="font-semibold text-lg">{form.id ? 'Editar' : 'Novo'} CFOP</h3></div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Código</label>
                  <input className="input" value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value })} placeholder="5102" /></div>
                <div><label className="label">Tipo</label>
                  <select className="input" value={form.tipo || ''} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                    <option value="saida">Saída</option><option value="entrada">Entrada</option>
                  </select></div>
              </div>

              {consolidado ? (
                <div>
                  <label className="label">Filial</label>
                  <select className="input" value={form.filial_id ?? ''}
                    onChange={e => setForm({ ...form, filial_id: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">Todas as filiais</option>
                    {filiais.filter((f: any) => f.ativo !== false).map((f: any) => (
                      <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-ink-500 mt-0.5">
                    Em branco, o CFOP aparece em todas as lojas.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-ink-500">
                  Este CFOP será criado em{' '}
                  <strong>{filialAtual?.nome_fantasia || filialAtual?.razao_social}</strong>.
                </p>
              )}

              <div><label className="label">Descrição</label>
                <input className="input" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
              <div><label className="label">CST sugerido (opcional)</label>
                <input className="input" value={form.cst_sugerido || ''} onChange={e => setForm({ ...form, cst_sugerido: e.target.value })} placeholder="10" /></div>
              <div><label className="label">Observação</label>
                <input className="input" value={form.observacao || ''} onChange={e => setForm({ ...form, observacao: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} /> Ativo
              </label>

              {/* ===== Nota avulsa ===== */}
              <div className="pt-3 mt-1 border-t space-y-3">
                <div>
                  <div className="text-sm font-semibold text-ink-800">Usar na Nota Fiscal Avulsa</div>
                  <p className="text-[11px] text-ink-500">
                    Preenchendo a natureza, este CFOP vira uma opção no seletor "O que é esta nota?".
                    Deixe em branco se for só catálogo de consulta.
                  </p>
                </div>
                <div><label className="label">Natureza da operação</label>
                  <input className="input" value={form.natureza_padrao || ''} placeholder="Ex.: Transferencia de mercadoria"
                    onChange={e => setForm({ ...form, natureza_padrao: e.target.value })} /></div>

                {form.natureza_padrao ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className="label">CFOP da outra UF</label>
                        <input className="input font-mono" value={form.cfop_par || ''} placeholder="6152"
                          title="O equivalente pra fora do estado (5152 → 6152). O sistema escolhe sozinho pela UF do destinatário."
                          onChange={e => setForm({ ...form, cfop_par: e.target.value })} /></div>
                      <div><label className="label">Finalidade</label>
                        <select className="input" value={form.finalidade || '1'}
                          onChange={e => setForm({ ...form, finalidade: e.target.value })}>
                          <option value="1">Normal</option><option value="2">Complementar</option>
                          <option value="3">Ajuste</option><option value="4">Devolução</option>
                        </select></div>
                      <div><label className="label">Consumidor final</label>
                        <select className="input" value={form.consumidor_final || ''}
                          onChange={e => setForm({ ...form, consumidor_final: e.target.value })}>
                          <option value="">(automático)</option>
                          <option value="0">Não</option><option value="1">Sim</option>
                        </select></div>
                    </div>
                    <div className="flex gap-5">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.gera_estoque}
                          onChange={e => setForm({ ...form, gera_estoque: e.target.checked })} /> Movimenta estoque
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.gera_financeiro}
                          onChange={e => setForm({ ...form, gera_financeiro: e.target.checked })} /> Gera financeiro
                      </label>
                    </div>
                    <div className="border-t pt-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.exige_retorno}
                          onChange={e => setForm({ ...form, exige_retorno: e.target.checked })} />
                        A mercadoria tem que voltar (conserto, garantia, demonstração)
                      </label>
                      <p className="text-[11px] text-ink-500 mt-0.5">
                        O ICMS sai suspenso e a suspensão cai se não retornar no prazo.
                        O sistema passa a acompanhar essas remessas.
                      </p>
                      {form.exige_retorno && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div><label className="label">CFOP que fecha o ciclo</label>
                            <input className="input font-mono" value={form.cfop_retorno || ''} placeholder="5916"
                              onChange={e => setForm({ ...form, cfop_retorno: e.target.value })} /></div>
                          <div><label className="label">Prazo (dias)</label>
                            <input type="number" className="input" value={form.prazo_retorno_dias ?? 180}
                              onChange={e => setForm({ ...form, prazo_retorno_dias: Number(e.target.value) })} /></div>
                        </div>
                      )}
                    </div>

                    <div><label className="label">Dica pro operador</label>
                      <input className="input" value={form.observacao_operacao || ''}
                        placeholder="Ex.: Baixa o estoque da loja que emite"
                        onChange={e => setForm({ ...form, observacao_operacao: e.target.value })} /></div>
                  </>
                ) : null}
              </div>
            </div>
            <div className="px-5 py-4 border-t flex gap-2 shrink-0">
              <button onClick={() => setForm(null)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => salvar.mutate(form)} disabled={!form.codigo || !form.descricao || salvar.isPending} className="btn-primary flex-1">
                {form.id ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
